/**
 * The message catalog (`messages/*.json`) is mostly flat next-intl extract
 * hashes (`{ "85lhj2": "…" }`), but it also carries explicit-keyed nested
 * namespaces — e.g. `email.auth.recovery.subject` for the localized emails.
 *
 * Several legacy surfaces (the flat strings editor, the coverage audit, the
 * translate-iframe inverse map) only understand the flat hash entries. Until
 * those surfaces grow dotted-namespace support, they consume the catalog
 * through `flatStringEntries`, which keeps only the top-level string-valued
 * entries and drops nested-object namespaces. This is behaviour-preserving for
 * the flat consumers: before the `email.*` namespace existed the catalog was
 * entirely flat strings, so the filtered view is exactly what they saw.
 */

export function flatStringEntries(messages: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [key, value] of Object.entries(messages)) {
    if (typeof value === 'string') out[key] = value
  }
  return out
}

/**
 * Full leaf view: keeps top-level flat hashes (`"85lhj2"`) AND flattens nested
 * namespaces into dotted keys (`email.auth.recovery.subject`). This is what
 * surfaces (the backstage strings editor) use to list and edit BOTH the
 * extracted-hash strings and the explicit `email.*` keys. The override write
 * path stores the dotted key as `message_key`; `applyOverrides` walks dotted
 * paths on read, so the round-trip works without any backend change.
 */
export function dottedStringEntries(messages: Record<string, unknown>, prefix = ''): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [key, value] of Object.entries(messages)) {
    const full = prefix ? `${prefix}.${key}` : key
    if (typeof value === 'string') {
      out[full] = value
    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(out, dottedStringEntries(value as Record<string, unknown>, full))
    }
  }
  return out
}
